import React, { forwardRef } from "react";
import PropTypes from "prop-types";
// form
import { Controller, useFormContext } from "react-hook-form";
// react-flatpickr components
import Flatpickr from "react-flatpickr";
// react-flatpickr styles
import "flatpickr/dist/flatpickr.css";

import SoftInput from "../SoftInput";
import SoftBox from "../SoftBox";
import SoftTypography from "../SoftTypography";

import { useTimeZoneStore } from "../../stores/timeZone";
import { fromPickerDate, toPickerDate } from "../../utils/timeZone";

/** Shows dates in the selected timezone while storing actual instants in the form. */
const DatePickerField = forwardRef(
  ({ options, input, name, label, placeholder, disabled, ...rest }, ref) => {
    const { control } = useFormContext();
    const timeZone = useTimeZoneStore((state) => state.timeZone);
    // Calendar limits use midnight in the selected zone, rather than the browser's day.
    const pickerLimit = (value) => {
      if (value === "today") {
        const date = toPickerDate(new Date(), timeZone);
        date.setHours(0, 0, 0, 0);
        return date;
      }
      return value instanceof Date || (typeof value === "string" && /^\d{4}-/.test(value))
        ? toPickerDate(value, timeZone)
        : value;
    };

    return (
      <SoftBox
        display="flex"
        flexDirection="column"
        justifyContent="flex-start"
        height="100%"
        mb={1.5}
      >
        {label && (
          <SoftBox mb={1} lineHeight={0} display="inline-block">
            <SoftTypography
              component="label"
              htmlFor={name}
              variant="caption"
              fontWeight="bold"
              textTransform="capitalize"
            >
              {label} ({timeZone})
            </SoftTypography>
          </SoftBox>
        )}
        <SoftBox mb={1} lineHeight={0}>
          <Controller
            name={name}
            control={control}
            render={({ field: { ref: formRef, ...formRest }, fieldState: { error } }) => (
              <>
                <Flatpickr
                  {...formRest}
                  required
                  value={toPickerDate(formRest.value, timeZone)}
                  options={{
                    defaultHour: 0,
                    defaultMinute: 0,
                    ...options,
                    ...(options.minDate && { minDate: pickerLimit(options.minDate) }),
                    ...(options.maxDate && { maxDate: pickerLimit(options.maxDate) }),
                  }}
                  onChange={(selectedDates) => {
                    const value = fromPickerDate(selectedDates[0], timeZone);
                    if (rest.onChange) {
                      rest.onChange(value);
                    }
                    formRest.onChange(value);
                  }}
                  render={({ defaultValue }, ref) => (
                    <SoftInput
                      {...input}
                      id={name}
                      error={Boolean(error || rest.error || input.error)}
                      defaultValue={null}
                      inputRef={ref}
                      placeholder={placeholder}
                      disabled={disabled}
                    />
                  )}
                  disabled={disabled}
                />
                {error && (
                  <SoftBox mt={0.75}>
                    <SoftTypography component="div" variant="caption" color="error">
                      {error?.message}
                    </SoftTypography>
                  </SoftBox>
                )}
              </>
            )}
          />
        </SoftBox>
      </SoftBox>
    );
  }
);

// Setting default values for the props of SoftInput
DatePickerField.defaultProps = {
  label: "",
  message: "",
  error: false,
  input: {},
  options: {},
  placeholder: "",
  disabled: false,
};

// Typechecking props for the SoftInput
DatePickerField.propTypes = {
  label: PropTypes.string,
  message: PropTypes.string,
  error: PropTypes.bool,
  name: PropTypes.string.isRequired,
  input: PropTypes.objectOf(PropTypes.any),
  options: PropTypes.objectOf(PropTypes.any).isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
};

export default DatePickerField;
